import {
  Skeleton,
  SkeletonAvatar,
  SkeletonButton,
  SkeletonField,
  SkeletonText,
} from "./SkeletonPrimitives";

/**
 * Full-page skeleton that mirrors the create-patient intake form layout.
 * Used as the loading state for /home, /intake, and navigating-to-intake transitions.
 */
export function PageSkeleton() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Avatar */}
      <div className="flex justify-center py-4">
        <SkeletonAvatar size="xl" />
      </div>

      {/* Headline + body text */}
      <div className="flex flex-col px-4 mb-6 gap-2">
        <SkeletonText size="xl" width="w-3/4" />
        <SkeletonText size="sm" width="w-full" />
        <SkeletonText size="sm" width="w-5/6" />
      </div>

      {/* Form sections */}
      <div className="flex flex-col gap-8 px-4 pb-3">
        {/* Section 1: Patient info — section label + 2 rows of paired fields */}
        <div className="flex flex-col gap-4 animate-fadeInUp">
          <SkeletonText size="lg" width="w-2/5" />
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <SkeletonField labelWidth="w-20" />
            </div>
            <div className="flex-1">
              <SkeletonField labelWidth="w-16" />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <SkeletonField labelWidth="w-24" />
            </div>
            <div className="flex-1">
              <SkeletonField labelWidth="w-8" />
            </div>
          </div>
        </div>

        {/* Section 2: Address — 2 full-width fields + 2 side-by-side */}
        <div
          className="flex flex-col gap-4 animate-fadeInUp"
          style={{ animationDelay: "0.06s" }}
        >
          <SkeletonField labelWidth="w-16" />
          <SkeletonField labelWidth="w-10" />
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <SkeletonField labelWidth="w-12" />
            </div>
            <div className="flex-1">
              <SkeletonField labelWidth="w-8" />
            </div>
          </div>
        </div>

        {/* Section 3: Contact — 2 full-width fields */}
        <div
          className="flex flex-col gap-4 animate-fadeInUp"
          style={{ animationDelay: "0.12s" }}
        >
          <SkeletonText size="lg" width="w-3/5" />
          <SkeletonField labelWidth="w-36" />
          <SkeletonField labelWidth="w-24" />
        </div>

        {/* Buttons */}
        <div
          className="flex flex-col gap-4 animate-fadeInUp"
          style={{ animationDelay: "0.18s" }}
        >
          <SkeletonButton variant="primary" />
          <Skeleton className="h-11 w-full rounded-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Precursor to PageSkeleton for coming from the SDK directly via triaged-cta
 * Used as the loading state for /home, /intake, and navigating-to-intake transitions.
 */
export function CreatingPatientPageSkeleton() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Avatar */}
      <div className="flex justify-center py-4 animate-fadeInUp">
        <SkeletonAvatar size="xl" />
      </div>

      {/* Headline + body text */}
      <div
        className="flex flex-col px-4 mb-6 gap-2 animate-fadeInUp"
        style={{ animationDelay: "0.1s" }}
      >
        <SkeletonText size="xl" width="w-3/4" />
        <SkeletonText size="sm" width="w-full" />
        <SkeletonText size="sm" width="w-5/6" />
      </div>
    </div>
  );
}
